import React, { useState, useEffect } from "react";
import { Card, Button, Table, Space, Modal, message, Badge, Dropdown, Tooltip, Tag, Divider } from "antd";
import { PlusOutlined, UploadOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined, DownOutlined, ClockCircleOutlined, EyeOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useLoadList, useDelete } from "../../../hooks";
import { useTableSelection } from "../../../hooks";
import { masterDataService } from "../../../api/enhancedApi";
import { formatDateTime, formatMinutesToHHMM } from "../../../utils/dateUtils";
import AirportFilter from "./components/AirportFilter";
import AirportForm from "./components/AirportForm";
import CurfewRulesViewer from "./components/CurfewRulesViewer";
import { useAuth } from "../../../hooks/useAuth";
import PermissionGuard from "../../../components/auth/PermissionGuard";
import TimezoneInfo from "../../../components/TimezoneInfo";
import { useTimeZone } from "../../../contexts/TimeZoneContext";

const { confirm } = Modal;

const AirportList = () => {
    // 获取权限相关功能
    const { hasPermission } = useAuth();

    // 时区上下文
    const { timeZone } = useTimeZone();

    // 使用机场数据hook
    // const { airports, loading: airportsLoading } = useAirports(); // 移除

    // 状态定义
    const [filterParams, setFilterParams] = useState({});
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 30,
    });
    const [formVisible, setFormVisible] = useState(false);
    const [currentAirport, setCurrentAirport] = useState(null);
    const [formMode, setFormMode] = useState("add"); // 'add' 或 'edit'
    const [curfewViewerVisible, setCurfewViewerVisible] = useState(false);
    const [selectedAirport, setSelectedAirport] = useState(null);

    // 使用自定义Hook加载数据
    const { data, isLoading, refetch } = useLoadList("airports", masterDataService.airport.getAirportList, {
        page: pagination.current,
        page_size: pagination.pageSize,
        ...filterParams,
    });

    // 使用自定义Hook处理表格选择
    const { selectedRowKeys, selectedRows, tableRef, rowSelection, getIndexColumn, getRowProps, clearSelection } = useTableSelection({
        rowKey: "iata_code",
        dataSource: data?.items || [],
        pagination,
        enableGlobalTracking: true, // 启用全局鼠标跟踪
        mouseMoveThrottle: 30, // 设置节流时间为30ms，可以根据实际性能调整
    });

    // 删除机场的Hook
    const deleteMutation = useDelete("airports", masterDataService.airport.deleteAirport, {
        onSuccess: () => {
            message.success("删除成功");
            refetch();
        },
    });

    // 查看宵禁规则详情
    const handleViewCurfewRules = (record) => {
        setSelectedAirport(record);
        setCurfewViewerVisible(true);
    };

    // 表格列定义
    const columns = [
        getIndexColumn("序号", 60),
        {
            title: "IATA代码",
            dataIndex: "iata_code",
            key: "iata_code",
            width: 80,
        },
        {
            title: "ICAO代码",
            dataIndex: "icao_code",
            key: "icao_code",
            width: 80,
        },
        {
            title: "机场名称",
            dataIndex: "airport_name",
            key: "airport_name",
            width: 180,
            ellipsis: true,
        },
        {
            title: "所在国家/地区",
            dataIndex: "country",
            key: "country",
            width: 100,
            ellipsis: true,
        },
        {
            title: "本国属性",
            dataIndex: "is_domestic",
            key: "is_domestic",
            width: 100,
            render: (isDomestic) => <Tag color={isDomestic ? "green" : "blue"}>{isDomestic ? "本国" : "境外"}</Tag>,
        },
        {
            title: "时刻许可",
            dataIndex: "requires_slot_permit",
            key: "requires_slot_permit",
            width: 100,
            render: (requiresSlotPermit) => <Tag color={requiresSlotPermit ? "orange" : "default"}>{requiresSlotPermit ? "需要" : "不需要"}</Tag>,
        },
        {
            title: "时区",
            dataIndex: "timezone",
            key: "timezone",
            width: 120,
            render: (timezone, record) => <TimezoneInfo timezone={timezone} utcOffsetString={record.utc_offset_string} utcOffsetMinutes={record.utc_offset_minutes} />,
        },
        {
            title: "最小过站时间",
            dataIndex: "turnaround_time",
            key: "turnaround_time",
            width: 120,
            render: (time) => formatMinutesToHHMM(time),
            tooltip: "显示格式为 小时:分钟",
        },
        {
            title: "宵禁规则",
            key: "curfew_rules",
            width: 200,
            render: (_, record) => {
                const hasDepRules = record.dep_curfew_rules && record.dep_curfew_rules.length > 0;
                const hasArrRules = record.arr_curfew_rules && record.arr_curfew_rules.length > 0;

                if (!hasDepRules && !hasArrRules) {
                    return <Tag color="default">无</Tag>;
                }

                return (
                    <Space>
                        {hasDepRules && (
                            // 增加hover样式
                            <Tooltip title={`${record.dep_curfew_rules.length}条起飞宵禁规则，点击查看详情`} onClick={() => handleViewCurfewRules(record, "dep")}>
                                <Tag className="icon-tag tag-red">
                                    <ClockCircleOutlined /> 起飞
                                </Tag>
                            </Tooltip>
                        )}
                        {hasArrRules && (
                            <Tooltip title={`${record.arr_curfew_rules.length}条降落宵禁规则，点击查看详情`} onClick={() => handleViewCurfewRules(record, "arr")}>
                                <Tag className="icon-tag tag-orange">
                                    <ClockCircleOutlined /> 降落
                                </Tag>
                            </Tooltip>
                        )}
                    </Space>
                );
            },
        },
        {
            title: "状态",
            dataIndex: "is_active",
            key: "is_active",
            width: 100,
            render: (is_active) => <Badge className="status-badge" status={is_active ? "success" : "error"} text={is_active ? "启用" : "停用"} />,
        },
        {
            title: "更新时间",
            dataIndex: "updated_at",
            key: "updated_at",
            width: 180,
            render: (text) => formatDateTime(text),
        },
        {
            title: "操作",
            key: "action",
            fixed: "right",
            width: 150,
            render: (_, record) => (
                <Space size="small" className="table-actions">
                    <PermissionGuard permission="masterdata:update">
                        <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
                            编辑
                        </Button>
                    </PermissionGuard>
                    <PermissionGuard permission="masterdata:delete">
                        <Button type="link" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(record)} disabled={record.is_active}>
                            删除
                        </Button>
                    </PermissionGuard>
                </Space>
            ),
        },
    ];

    // 处理表格分页变化
    const handleTableChange = (newPagination, filters, sorter) => {
        setPagination(newPagination);
    };

    // 处理搜索
    const handleSearch = (values) => {
        // 处理搜索参数，确保与后端API参数匹配
        const params = {};

        if (values.code) {
            params.iata_code = values.code;
        }

        if (values.icao_code) {
            params.icao_code = values.icao_code;
        }

        if (values.city) {
            params.city = values.city;
        }

        if (values.country) {
            params.country = values.country;
        }

        if (values.status !== undefined) {
            params.is_active = values.status === "active";
        }

        setFilterParams(params);
        setPagination({
            ...pagination,
            current: 1, // 重置到第一页
        });
    };

    // 重置搜索
    const handleReset = () => {
        setFilterParams({});
        setPagination({
            ...pagination,
            current: 1,
        });
    };

    // 添加机场
    const handleAdd = () => {
        // 检查创建权限
        if (!hasPermission("masterdata:create")) {
            message.error("您没有创建机场的权限");
            return;
        }

        setFormMode("add");
        setCurrentAirport(null);
        setFormVisible(true);
    };

    // 编辑机场
    const handleEdit = (record) => {
        // 检查更新权限
        if (!hasPermission("masterdata:update")) {
            message.error("您没有更新机场的权限");
            return;
        }

        setFormMode("edit");
        setCurrentAirport(record);
        setFormVisible(true);
    };

    // 删除机场
    const handleDelete = (record) => {
        // 检查删除权限
        if (!hasPermission("masterdata:delete")) {
            message.error("您没有删除机场的权限");
            return;
        }

        if (record.is_active) {
            message.warning("启用状态的机场不能删除，请先停用");
            return;
        }

        confirm({
            title: "确认删除",
            icon: <ExclamationCircleOutlined />,
            content: `确定要删除机场 ${record.airport_name}(${record.iata_code})？此操作不可恢复。`,
            okText: "确认",
            okType: "danger",
            cancelText: "取消",
            onOk() {
                deleteMutation.mutate(record.iata_code);
            },
        });
    };

    // 批量删除机场
    const handleBatchDelete = () => {
        // 检查删除权限
        if (!hasPermission("masterdata:delete")) {
            message.error("您没有删除机场的权限");
            return;
        }

        const activeAirports = selectedRows.filter((item) => item.is_active);
        if (activeAirports.length > 0) {
            message.warning("选择的机场中包含启用状态的机场，请先停用后再删除");
            return;
        }

        confirm({
            title: "确认批量删除",
            icon: <ExclamationCircleOutlined />,
            content: `确定要删除选中的 ${selectedRowKeys.length} 个机场吗？此操作不可恢复。`,
            okText: "确认",
            okType: "danger",
            cancelText: "取消",
            onOk() {
                // 串行执行删除操作
                Promise.all(selectedRowKeys.map((key) => masterDataService.airport.deleteAirport(key)))
                    .then(() => {
                        message.success(`成功删除 ${selectedRowKeys.length} 个机场`);
                        clearSelection();
                        refetch();
                    })
                    .catch((error) => {
                        message.error(`批量删除失败: ${error.message}`);
                    });
            },
        });
    };

    // 批量启用机场
    const handleBatchEnable = () => {
        // 检查更新权限
        if (!hasPermission("masterdata:update")) {
            message.error("您没有更新机场的权限");
            return;
        }

        // 这里假设有批量启用的API，实际情况可能需要调整
        message.info("批量启用功能正在开发中");
    };

    // 批量停用机场
    const handleBatchDisable = () => {
        // 检查更新权限
        if (!hasPermission("masterdata:update")) {
            message.error("您没有更新机场的权限");
            return;
        }

        // 这里假设有批量停用的API，实际情况可能需要调整
        message.info("批量停用功能正在开发中");
    };

    // 批量操作菜单项
    const batchActionItems = [
        {
            key: "delete",
            label: "批量删除",
            disabled: selectedRowKeys.length === 0 || !hasPermission("masterdata:delete"),
            onClick: handleBatchDelete,
        },
        {
            key: "enable",
            label: "批量启用",
            disabled: selectedRowKeys.length === 0 || !hasPermission("masterdata:update"),
            onClick: handleBatchEnable,
        },
        {
            key: "disable",
            label: "批量停用",
            disabled: selectedRowKeys.length === 0 || !hasPermission("masterdata:update"),
            onClick: handleBatchDisable,
        },
    ];

    // 表单提交成功
    const handleFormSuccess = () => {
        setFormVisible(false);
        refetch();
    };

    return (
        <div className="page-container">
            {/* 页面标题区域 - 使用全局样式类 */}
            <div className="page-header-compact">
                <h3 className="page-title-compact">机场管理</h3>
                <div className="page-action-compact">
                    <PermissionGuard permission="masterdata:create">
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                            添加机场
                        </Button>
                    </PermissionGuard>
                    <PermissionGuard permission="masterdata:delete">
                        <Dropdown menu={{ items: batchActionItems }} disabled={selectedRowKeys.length === 0}>
                            <Button>
                                批量操作 <DownOutlined />
                            </Button>
                        </Dropdown>
                    </PermissionGuard>
                </div>
            </div>

            {/* 过滤器 - 使用filter-card类 */}
            <Card className="filter-card">
                <AirportFilter onSearch={handleSearch} onReset={handleReset} />
            </Card>

            {/* 选中项统计 - 使用batch-operations-info类 */}
            {selectedRowKeys.length > 0 && (
                <div className="batch-operations-info">
                    已选择 <span className="selected-count">{selectedRowKeys.length}</span> 项
                    <Button type="link" onClick={clearSelection}>
                        清空
                    </Button>
                </div>
            )}

            {/* 机场列表 - 使用data-card类 */}
            <Card className="data-card">
                {data?.total === 0 && !isLoading ? (
                    <div className="data-empty">
                        <div className="data-empty-icon">
                            <ExclamationCircleOutlined />
                        </div>
                        <div className="data-empty-text">暂无数据</div>
                    </div>
                ) : (
                    <Table
                        ref={tableRef}
                        size="small"
                        rowSelection={hasPermission("masterdata:delete") || hasPermission("masterdata:update") ? rowSelection : null}
                        columns={columns}
                        dataSource={data?.items || []}
                        rowKey="iata_code"
                        pagination={{
                            ...pagination,
                            total: data?.total || 0,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total) => `共 ${total} 条记录`,
                        }}
                        loading={isLoading}
                        onChange={handleTableChange}
                        scroll={{ x: 1200 }}
                        onRow={getRowProps}
                    />
                )}
            </Card>

            {/* 添加/编辑机场表单 */}
            {formVisible && <AirportForm visible={formVisible} mode={formMode} initialValues={currentAirport} onCancel={() => setFormVisible(false)} onSuccess={handleFormSuccess} />}

            {/* 宵禁规则查看器 */}
            {curfewViewerVisible && <CurfewRulesViewer visible={curfewViewerVisible} onClose={() => setCurfewViewerVisible(false)} airport={selectedAirport} />}
        </div>
    );
};

export default AirportList;
